import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { toast } from "react-hot-toast";

import { ErrorState, Loading } from "../../components/WorkspacePrimitives";
import {
  BackIcon,
  Button,
  CopyIcon,
  IconButton,
  PlusIcon,
  RetryIcon,
  SendIcon,
  Selector,
  TextArea,
  type SelectorOption,
} from "../../components/ui";
import { copyText } from "../../lib/clipboard";
import {
  useAgentChatThread,
  useAgentChatThreads,
  useApproveAgentChatEditProposal,
  useAgentDocumentsByIds,
  useCreateAgentChatThread,
  useCreateAgentChatTurn,
  useRefreshAgentModelAfterChatEdit,
  useRejectAgentChatEditProposal,
  type AgentChatEditProposal,
  type AgentChatEditSurface,
  type AgentChatIntermediateItem,
  type AgentChatThreadListItem,
  type AgentChatToolCall,
  type AgentChatTurn,
  type AgentChatTurnMode,
  type AgentStoredDocument,
  type XLMModel,
} from "../api/e2e";
import { citedDocumentIds } from "../lib/agentSummary";
import { relativeTime } from "../lib/format";
import { storedModelChatMode, storeModelChatMode } from "../lib/modelChat";
import type { ModelReference } from "../lib/modelReferences";
import AgentChatWidget, { SHOW_WIDGET_TOOL_NAME } from "./AgentChatWidget";
import AgentSummaryDocumentPreview from "./AgentSummaryDocumentPreview";
import AgentSummaryMarkdown from "./AgentSummaryMarkdown";

const EMPTY_TURNS: AgentChatTurn[] = [];
const EDIT_MODEL_TOOL_NAME = "edit_model";
const IGNORE_EDIT_ACTIVITY = () => undefined;

const CHAT_MODE_OPTIONS: readonly SelectorOption<AgentChatTurnMode>[] = [
  {
    value: "ask",
    label: "Ask",
    secondary: "Analyze without changing the model.",
  },
  {
    value: "edit",
    label: "Edit",
    secondary: "Prepare model edits for approval.",
  },
  {
    value: "edit_autoapprove",
    label: "Edit (autoapprove)",
    secondary: "Apply model edits immediately.",
  },
];

const CHAT_MODE_TRIGGER_LABELS: Record<AgentChatTurnMode, string> = {
  ask: "Ask",
  edit: "Edit",
  edit_autoapprove: "Edit auto-approve",
};

interface AgentChatMarkdownContext {
  documents: AgentStoredDocument[];
  referenceState: "loading" | "ready" | "error";
  model: XLMModel | undefined;
  modelReferenceState: "loading" | "ready" | "error";
  openingDocumentId: string | null;
  onOpenDocument: (document: AgentStoredDocument) => void;
  onOpenModelReference: (reference: ModelReference) => void;
}

function proposalsForTurn(turn: AgentChatTurn): AgentChatEditProposal[] {
  const proposals: AgentChatEditProposal[] | undefined = turn.edit_proposals;
  return proposals ?? (turn.edit_proposal ? [turn.edit_proposal] : []);
}

function turnStatus(turn: AgentChatTurn): string | null {
  if (turn.state === "queued") return "Waiting for the model…";
  if (proposalsForTurn(turn).some((proposal) => proposal.status === "pending"))
    return "Waiting for analyst approval…";
  if (turn.state === "running") return "Reviewing the model and its sources…";
  return null;
}

function ChatModeIcon({ mode }: { mode: AgentChatTurnMode }) {
  if (mode === "ask") {
    return (
      <svg
        className="agent-model-chat__ask-mode-icon"
        viewBox="0 0 16 16"
        aria-hidden="true"
      >
        <path d="M3.25 3.5h9.5v7H7.5L4.25 13v-2.5h-1z" />
        <path d="M5.5 6.25h5M5.5 8.25h3.25" />
      </svg>
    );
  }
  return (
    <svg
      className="agent-model-chat__edit-mode-icon"
      viewBox="0 0 16 16"
      aria-hidden="true"
    >
      <path d="m3.25 11.5-.5 2 2-.5 7.5-7.5-1.5-1.5z" />
      <path d="m9.75 5 1.5 1.5" />
      {mode === "edit_autoapprove" && <path d="M12.25 8.25v3M10.75 9.75h3" />}
    </svg>
  );
}

function ChatModeTrigger({ mode }: { mode: AgentChatTurnMode }) {
  return (
    <>
      <ChatModeIcon mode={mode} />
      <span className="agent-model-chat__mode-label">
        {CHAT_MODE_TRIGGER_LABELS[mode]}
      </span>
    </>
  );
}

function formatToolPayload(payload: string): string {
  try {
    const parsed: unknown = JSON.parse(payload);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return payload;
  }
}

function TraceIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="3" cy="3.5" r="1" />
      <circle cx="3" cy="8" r="1" />
      <circle cx="3" cy="12.5" r="1" />
      <path d="M5.5 3.5H13M5.5 8H13M5.5 12.5H13" />
    </svg>
  );
}

function ToolEventIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="m6 4-3 4 3 4M10 4l3 4-3 4" />
    </svg>
  );
}

function AgentChatMarkdown({
  source,
  context,
}: {
  source: string;
  context: AgentChatMarkdownContext;
}) {
  return (
    <AgentSummaryMarkdown
      source={source}
      documents={context.documents}
      referenceState={context.referenceState}
      model={context.model}
      modelReferenceState={context.modelReferenceState}
      openingDocumentId={context.openingDocumentId}
      onOpenDocument={context.onOpenDocument}
      onOpenModelReference={context.onOpenModelReference}
    />
  );
}

function intermediatePresentation(item: AgentChatIntermediateItem): {
  title: string;
  markdown: string;
} {
  const titledMarkdown = item.text.match(
    /^\s*\*\*(.+?)\*\*\s*\r?\n(?:\s*\r?\n)+([\s\S]*)$/,
  );
  if (titledMarkdown) {
    return {
      title: titledMarkdown[1]?.trim() || "Reasoning",
      markdown: titledMarkdown[2]?.trim() ?? "",
    };
  }
  return {
    title: item.kind === "reasoning_summary" ? "Reasoning" : "Assistant text",
    markdown: item.text,
  };
}

function AgentChatIntermediateTraceItem({
  item,
  markdownContext,
}: {
  item: AgentChatIntermediateItem;
  markdownContext: AgentChatMarkdownContext;
}) {
  const presentation = intermediatePresentation(item);
  return (
    <article
      className="agent-model-chat__intermediate-item"
      aria-label={presentation.title}
    >
      <header>
        <strong>{presentation.title}</strong>
      </header>
      {presentation.markdown && (
        <div className="agent-model-chat__intermediate-text">
          <AgentChatMarkdown
            source={presentation.markdown}
            context={markdownContext}
          />
        </div>
      )}
    </article>
  );
}

type AgentChatTraceEvent =
  | {
      kind: "intermediate";
      round: number;
      sequence: number;
      item: AgentChatIntermediateItem;
    }
  | {
      kind: "tool";
      round: number;
      sequence: number;
      toolCall: AgentChatToolCall;
    };

function agentChatTraceEvents(
  items: AgentChatIntermediateItem[],
  toolCalls: AgentChatToolCall[],
): AgentChatTraceEvent[] {
  const events: AgentChatTraceEvent[] = [
    ...items.map((item) => ({
      kind: "intermediate" as const,
      round: item.round,
      sequence: item.sequence,
      item,
    })),
    ...toolCalls
      .filter((toolCall) => toolCall.name !== SHOW_WIDGET_TOOL_NAME)
      .map((toolCall) => ({
        kind: "tool" as const,
        round: toolCall.round,
        sequence: toolCall.sequence,
        toolCall,
      })),
  ];
  return events.sort((left, right) => {
    if (left.round !== right.round) return left.round - right.round;
    if (left.kind !== right.kind) return left.kind === "intermediate" ? -1 : 1;
    return left.sequence - right.sequence;
  });
}

function AgentChatEditProposalCard({
  proposal,
  approving,
  rejecting,
  onApprove,
  onReject,
}: {
  proposal: AgentChatEditProposal;
  approving: boolean;
  rejecting: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  const pending = proposal.status === "pending";
  return (
    <section
      className={`agent-model-chat__edit-proposal agent-model-chat__edit-proposal--${proposal.status}`}
      aria-label="Model edit proposal"
    >
      <header>
        <strong>{proposal.description}</strong>
        <span>
          {proposal.operations.length}{" "}
          {proposal.operations.length === 1 ? "operation" : "operations"}
        </span>
      </header>
      <p>
        Revision {proposal.base_revision}
        {proposal.applied_revision !== null
          ? ` → ${proposal.applied_revision}`
          : ""}
      </p>
      {proposal.finding_count > 0 && (
        <small>
          The validated result retains {proposal.finding_count} model{" "}
          {proposal.finding_count === 1 ? "finding" : "findings"}.
        </small>
      )}
      {proposal.safe_message && <small>{proposal.safe_message}</small>}
      {pending ? (
        <div className="agent-model-chat__edit-actions">
          <Button
            tone="primary"
            size="sm"
            loading={approving}
            disabled={rejecting}
            onClick={onApprove}
          >
            Approve
          </Button>
          <Button
            tone="quiet"
            size="sm"
            loading={rejecting}
            disabled={approving}
            onClick={onReject}
          >
            Reject
          </Button>
        </div>
      ) : (
        <strong className="agent-model-chat__edit-status">
          {proposal.status === "applied"
            ? "Applied"
            : proposal.status === "rejected"
              ? "Rejected"
              : proposal.status === "stale"
                ? "Model changed"
                : "Not applied"}
        </strong>
      )}
    </section>
  );
}

function AgentChatEditToolCall({
  toolCall,
  proposal,
  approving,
  rejecting,
  onApprove,
  onReject,
}: {
  toolCall: AgentChatToolCall;
  proposal: AgentChatEditProposal | null;
  approving: boolean;
  rejecting: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  let status = "Complete";
  if (proposal?.status === "pending") {
    status = "Waiting for approval";
  } else if (proposal?.status === "rejected") {
    status = "Complete";
  } else if (proposal && toolCall.output === null) {
    status = "Resuming agent";
  } else if (toolCall.output === null) {
    status = "Preparing batch";
  }
  return (
    <article
      className="agent-model-chat__edit-tool-call"
      aria-label={`Tool call ${toolCall.sequence}: ${toolCall.name}`}
    >
      <header>
        <code>{toolCall.name}</code>
        <span>{status}</span>
      </header>
      {proposal && (
        <AgentChatEditProposalCard
          proposal={proposal}
          approving={approving}
          rejecting={rejecting}
          onApprove={onApprove}
          onReject={onReject}
        />
      )}
    </article>
  );
}

function AgentChatTrace({
  items,
  toolCalls,
  live,
  markdownContext,
  editProposals,
  approvingEdit,
  rejectingEdit,
  onApproveEdit,
  onRejectEdit,
}: {
  items: AgentChatIntermediateItem[];
  toolCalls: AgentChatToolCall[];
  live: boolean;
  markdownContext: AgentChatMarkdownContext;
  editProposals: AgentChatEditProposal[];
  approvingEdit: boolean;
  rejectingEdit: boolean;
  onApproveEdit: (proposal: AgentChatEditProposal) => void;
  onRejectEdit: (proposal: AgentChatEditProposal) => void;
}) {
  const [expanded, setExpanded] = useState(live);
  const events = agentChatTraceEvents(items, toolCalls);
  if (events.length === 0) return null;
  return (
    <details
      className="agent-model-chat__trace"
      open={expanded}
      onToggle={(event) => setExpanded(event.currentTarget.open)}
    >
      <summary>
        <span className="agent-model-chat__trace-icon">
          <TraceIcon />
        </span>
        <strong>Trace</strong>
        <small>
          {events.length} {events.length === 1 ? "event" : "events"}
        </small>
      </summary>
      {expanded && (
        <div className="agent-model-chat__trace-list">
          {events.map((event) =>
            event.kind === "intermediate" ? (
              <AgentChatIntermediateTraceItem
                key={`intermediate-${event.item.sequence}`}
                item={event.item}
                markdownContext={markdownContext}
              />
            ) : event.toolCall.name === EDIT_MODEL_TOOL_NAME ? (
              <div
                key={`tool-${event.toolCall.sequence}-${event.toolCall.call_id}`}
                className="agent-model-chat__tool-event"
              >
                <span
                  className="agent-model-chat__tool-event-marker"
                  aria-hidden="true"
                >
                  <ToolEventIcon />
                </span>
                <AgentChatEditToolCall
                  toolCall={event.toolCall}
                  proposal={
                    editProposals.find(
                      (proposal) =>
                        proposal.tool_call_id === event.toolCall.call_id,
                    ) ?? null
                  }
                  approving={
                    approvingEdit &&
                    editProposals.some(
                      (proposal) =>
                        proposal.tool_call_id === event.toolCall.call_id &&
                        proposal.status === "pending",
                    )
                  }
                  rejecting={
                    rejectingEdit &&
                    editProposals.some(
                      (proposal) =>
                        proposal.tool_call_id === event.toolCall.call_id &&
                        proposal.status === "pending",
                    )
                  }
                  onApprove={() => {
                    const proposal = editProposals.find(
                      (item) => item.tool_call_id === event.toolCall.call_id,
                    );
                    if (proposal) onApproveEdit(proposal);
                  }}
                  onReject={() => {
                    const proposal = editProposals.find(
                      (item) => item.tool_call_id === event.toolCall.call_id,
                    );
                    if (proposal) onRejectEdit(proposal);
                  }}
                />
              </div>
            ) : (
              <div
                key={`tool-${event.toolCall.sequence}-${event.toolCall.call_id}`}
                className="agent-model-chat__tool-event"
              >
                <span
                  className="agent-model-chat__tool-event-marker"
                  aria-hidden="true"
                >
                  <ToolEventIcon />
                </span>
                <AgentChatToolCallTrace toolCall={event.toolCall} live={live} />
              </div>
            ),
          )}
        </div>
      )}
    </details>
  );
}

function AgentChatToolCallTrace({
  toolCall,
  live,
}: {
  toolCall: AgentChatToolCall;
  live: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const output = toolCall.output;
  const pending = output === null;
  return (
    <details
      className="agent-model-chat__tool-call"
      open={expanded}
      onToggle={(event) => setExpanded(event.currentTarget.open)}
      aria-label={`Tool call ${toolCall.sequence}: ${toolCall.name}`}
    >
      <summary>
        <span className="agent-model-chat__tool-identity">
          <code>{toolCall.name}</code>
        </span>
        <span
          className={`agent-model-chat__tool-status agent-model-chat__tool-status--${pending ? "pending" : "complete"}`}
        >
          {pending && live ? "Running" : pending ? "No output" : "Complete"}
        </span>
      </summary>
      {expanded && (
        <div className="agent-model-chat__tool-payload">
          <small className="agent-model-chat__tool-call-id">
            {toolCall.call_id}
          </small>
          <strong>Arguments</strong>
          <pre>
            <code>{formatToolPayload(toolCall.arguments)}</code>
          </pre>
          <strong>Output</strong>
          {pending ? (
            <p className="agent-model-chat__tool-pending">
              {live
                ? "Waiting for tool output…"
                : "Tool output was not recorded."}
            </p>
          ) : (
            <pre>
              <code>{formatToolPayload(output)}</code>
            </pre>
          )}
        </div>
      )}
    </details>
  );
}

function AgentChatHistory({
  threads,
  selectedThreadId,
  persistent = false,
  creatingThread = false,
  navigationLocked = false,
  onNewChat,
  onSelect,
}: {
  threads: AgentChatThreadListItem[];
  selectedThreadId: string | null;
  persistent?: boolean;
  creatingThread?: boolean;
  navigationLocked?: boolean;
  onNewChat?: () => void;
  onSelect: (threadId: string) => void;
}) {
  return (
    <section
      id="agent-model-chat-history"
      className={[
        "agent-model-chat__history",
        persistent ? "agent-model-chat__history--persistent" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-labelledby="agent-model-chat-history-title"
    >
      <header className="agent-model-chat__history-header">
        <h3 id="agent-model-chat-history-title">Chat history</h3>
        {onNewChat && (
          <IconButton
            type="button"
            tone="quiet"
            size="sm"
            label="Start new chat"
            tooltip="Start new chat"
            icon={<PlusIcon />}
            disabled={creatingThread || navigationLocked}
            onClick={onNewChat}
          />
        )}
      </header>
      {threads.length > 0 ? (
        <div className="agent-model-chat__thread-list">
          {threads.map((thread) => (
            <button
              key={thread.id}
              type="button"
              className={
                thread.id === selectedThreadId ? "is-active" : undefined
              }
              aria-current={thread.id === selectedThreadId ? "true" : undefined}
              disabled={navigationLocked && thread.id !== selectedThreadId}
              onClick={() => onSelect(thread.id)}
            >
              <strong>{thread.title ?? "New conversation"}</strong>
              <span>{relativeTime(thread.updated_at)}</span>
            </button>
          ))}
        </div>
      ) : (
        <p>No saved conversations yet.</p>
      )}
    </section>
  );
}

export default function AgentModelChat({
  companyId,
  versionId,
  isRegistered = true,
  model,
  modelReferenceState,
  revisionLocked = false,
  fullScreen = false,
  editSurface = "web",
  excelSessionKey,
  onEditActivityChange = IGNORE_EDIT_ACTIVITY,
  onOpenModelReference,
}: {
  companyId: string;
  versionId: string;
  isRegistered?: boolean;
  model: XLMModel | undefined;
  modelReferenceState: "loading" | "ready" | "error";
  revisionLocked?: boolean;
  fullScreen?: boolean;
  editSurface?: AgentChatEditSurface;
  excelSessionKey?: string;
  onEditActivityChange?: (active: boolean) => void;
  onOpenModelReference: (reference: ModelReference) => void;
}) {
  const threads = useAgentChatThreads(companyId, versionId, isRegistered);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [view, setView] = useState<"conversation" | "history">("conversation");
  const selectedThread = useAgentChatThread(selectedThreadId ?? "");
  const createThread = useCreateAgentChatThread();
  const createTurn = useCreateAgentChatTurn();
  const approveEdit = useApproveAgentChatEditProposal();
  const rejectEdit = useRejectAgentChatEditProposal();
  const refreshEditedModel = useRefreshAgentModelAfterChatEdit();
  const [mode, setMode] = useState<AgentChatTurnMode>(storedModelChatMode);
  const [message, setMessage] = useState("");
  const [modelRefreshPending, setModelRefreshPending] = useState(false);
  const [modelRefreshFailed, setModelRefreshFailed] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [previewDocument, setPreviewDocument] =
    useState<AgentStoredDocument | null>(null);
  const latestExchange = useRef<HTMLDivElement>(null);
  const transcriptEnd = useRef<HTMLDivElement>(null);
  const synchronizedProposals = useRef(new Set<string>());
  const submissionInFlight = useRef(false);

  useEffect(() => {
    if (!threads.data) return;
    if (
      selectedThreadId &&
      threads.data.some((thread) => thread.id === selectedThreadId)
    ) {
      return;
    }
    setSelectedThreadId(threads.data[0]?.id ?? null);
  }, [selectedThreadId, threads.data]);

  const turns = selectedThread.data?.turns ?? EMPTY_TURNS;
  const citedIds = useMemo(
    () =>
      citedDocumentIds(
        turns
          .flatMap((turn) => [
            turn.answer_markdown ?? "",
            ...turn.intermediate.map((item) => item.text),
          ])
          .filter(Boolean)
          .join("\n"),
      ),
    [turns],
  );
  const documents = useAgentDocumentsByIds(companyId, citedIds);
  const referenceState = documents.error
    ? "error"
    : citedIds.length > 0 && documents.isPending
      ? "loading"
      : "ready";
  const activeTurn = turns.some(
    (turn) => turn.state === "queued" || turn.state === "running",
  );
  const editProposals = useMemo(() => turns.flatMap(proposalsForTurn), [turns]);
  const pendingProposal = editProposals.find(
    (proposal) => proposal.status === "pending",
  );
  const unsynchronizedModelProposal = editProposals.some(
    (proposal) =>
      (proposal.status === "applied" || proposal.status === "stale") &&
      !synchronizedProposals.current.has(proposal.id),
  );
  const editActivity =
    ((createThread.isPending || createTurn.isPending) && mode !== "ask") ||
    turns.some(
      (turn) =>
        (turn.state === "queued" || turn.state === "running") &&
        turn.mode !== "ask",
    ) ||
    pendingProposal !== undefined ||
    approveEdit.isPending ||
    modelRefreshPending ||
    modelRefreshFailed ||
    unsynchronizedModelProposal;
  const submitting = createThread.isPending || createTurn.isPending;
  const markdownContext: AgentChatMarkdownContext = {
    documents: documents.data ?? [],
    referenceState,
    model,
    modelReferenceState,
    openingDocumentId: previewDocument?.id ?? null,
    onOpenDocument: setPreviewDocument,
    onOpenModelReference,
  };

  useEffect(() => {
    if (
      (threads.isPending && !threads.data) ||
      (selectedThreadId === null && (threads.data?.length ?? 0) > 0) ||
      (selectedThreadId !== null && !selectedThread.data)
    ) {
      return;
    }
    onEditActivityChange(editActivity);
  }, [
    editActivity,
    onEditActivityChange,
    selectedThread.data,
    selectedThreadId,
    threads.data,
    threads.isPending,
  ]);

  useEffect(() => {
    const updates: {
      proposal: AgentChatEditProposal;
      resultSnapshot: AgentChatTurn["result_snapshot"];
    }[] = [];
    for (const turn of turns) {
      for (const proposal of proposalsForTurn(turn)) {
        if (
          (proposal.status !== "applied" && proposal.status !== "stale") ||
          synchronizedProposals.current.has(proposal.id)
        ) {
          continue;
        }
        synchronizedProposals.current.add(proposal.id);
        updates.push({ proposal, resultSnapshot: turn.result_snapshot });
      }
    }
    if (updates.length === 0) return;
    setModelRefreshFailed(false);
    setModelRefreshPending(true);
    void refreshEditedModel(companyId, versionId, updates)
      .catch(() => {
        setModelRefreshFailed(true);
        toast.error(
          "The model changed, but its latest snapshot could not be refreshed. Reload this page before editing the grid.",
        );
      })
      .finally(() => setModelRefreshPending(false));
  }, [companyId, refreshEditedModel, turns, versionId]);

  useEffect(() => {
    if (activeTurn) {
      transcriptEnd.current?.scrollIntoView?.({ block: "end" });
      return;
    }
    latestExchange.current?.scrollIntoView?.({ block: "start" });
  }, [activeTurn, turns]);

  const startNewChat = async () => {
    if (!isRegistered || revisionLocked || editActivity) return;
    setSubmitError(null);
    try {
      const created = await createThread.mutateAsync({ companyId, versionId });
      setSelectedThreadId(created.id);
      setView("conversation");
      setMessage("");
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "A new chat could not be created.",
      );
    }
  };

  const sendMessage = async (rawMessage: string) => {
    const normalized = rawMessage.trim();
    if (
      !normalized ||
      !isRegistered ||
      activeTurn ||
      submitting ||
      revisionLocked ||
      pendingProposal !== undefined ||
      submissionInFlight.current
    ) {
      return;
    }
    submissionInFlight.current = true;
    setSubmitError(null);
    try {
      let threadId = selectedThreadId;
      if (!threadId) {
        const created = await createThread.mutateAsync({
          companyId,
          versionId,
        });
        threadId = created.id;
        setSelectedThreadId(created.id);
      }
      await createTurn.mutateAsync({
        threadId,
        message: normalized,
        mode: pendingProposal ? "ask" : mode,
        editSurface,
        excelSessionKey,
      });
      setMessage("");
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "The question could not be sent.",
      );
    } finally {
      submissionInFlight.current = false;
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void sendMessage(message);
  };

  const handleComposerKey = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    event.preventDefault();
    void sendMessage(message);
  };

  const copyChatText = (
    text: string,
    successMessage: string,
    failureMessage: string,
  ) => {
    void copyText(text)
      .then(() => toast.success(successMessage))
      .catch(() => toast.error(failureMessage));
  };

  if (!isRegistered) {
    return <Loading label="Preparing model chat…" />;
  }

  if (threads.isPending && !threads.data) {
    return <Loading label="Loading model conversations…" />;
  }
  if (threads.error && !threads.data) {
    return (
      <ErrorState error={threads.error} onRetry={() => threads.refetch()} />
    );
  }

  return (
    <section
      className={[
        "agent-model-chat",
        fullScreen ? "agent-model-chat--fullscreen" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label="Model chat"
    >
      {!fullScreen && (
        <header className="agent-model-chat__toolbar" aria-label="Chat actions">
          <IconButton
            type="button"
            tone="quiet"
            size="sm"
            label={
              view === "history" ? "Back to conversation" : "View chat history"
            }
            tooltip={
              view === "history" ? "Back to conversation" : "View chat history"
            }
            icon={<BackIcon />}
            disabled={editActivity}
            onClick={() =>
              setView((current) =>
                current === "history" ? "conversation" : "history",
              )
            }
          />
          <IconButton
            type="button"
            tone="quiet"
            size="sm"
            label="Start new chat"
            tooltip="Start new chat"
            icon={<PlusIcon />}
            disabled={createThread.isPending || revisionLocked || editActivity}
            onClick={() => void startNewChat()}
          />
        </header>
      )}

      {fullScreen && (
        <AgentChatHistory
          threads={threads.data ?? []}
          selectedThreadId={selectedThreadId}
          persistent
          creatingThread={createThread.isPending || revisionLocked}
          navigationLocked={editActivity}
          onNewChat={() => void startNewChat()}
          onSelect={(threadId) => {
            setSelectedThreadId(threadId);
          }}
        />
      )}

      {!fullScreen && view === "history" ? (
        <AgentChatHistory
          threads={threads.data ?? []}
          selectedThreadId={selectedThreadId}
          navigationLocked={editActivity}
          onSelect={(threadId) => {
            setSelectedThreadId(threadId);
            setView("conversation");
          }}
        />
      ) : (
        <div className="agent-model-chat__conversation">
          {selectedThreadId &&
          selectedThread.isPending &&
          !selectedThread.data ? (
            <Loading label="Opening conversation…" />
          ) : selectedThread.error && !selectedThread.data ? (
            <ErrorState
              error={selectedThread.error}
              onRetry={() => selectedThread.refetch()}
            />
          ) : (
            <>
              <div
                className="agent-model-chat__transcript"
                aria-live="polite"
                aria-label="Conversation transcript"
              >
                {turns.length === 0 && (
                  <div className="agent-model-chat__empty">
                    <p>
                      Ask about reported results, forecasts, formulas,
                      assumptions, or the documents supporting this published
                      model.
                    </p>
                    <small>
                      Ask for analysis or choose an edit mode to prepare atomic
                      model changes.
                    </small>
                  </div>
                )}
                {turns.map((turn, turnIndex) => {
                  const status = turnStatus(turn);
                  const turnEditProposals = proposalsForTurn(turn);
                  const pendingTurnProposal = turnEditProposals.find(
                    (proposal) => proposal.status === "pending",
                  );
                  return (
                    <div
                      className="agent-model-chat__exchange"
                      key={turn.id}
                      ref={
                        turnIndex === turns.length - 1
                          ? latestExchange
                          : undefined
                      }
                    >
                      <div className="agent-model-chat__analyst-row">
                        <IconButton
                          type="button"
                          tone="quiet"
                          size="sm"
                          label="Copy sent message"
                          tooltip="Copy"
                          icon={<CopyIcon />}
                          containerClassName="agent-model-chat__message-copy"
                          onClick={() =>
                            copyChatText(
                              turn.question,
                              "Message copied.",
                              "The message could not be copied.",
                            )
                          }
                        />
                        <div
                          className="agent-model-chat__message agent-model-chat__message--analyst"
                          aria-label="Sent message"
                        >
                          <p>{turn.question}</p>
                        </div>
                      </div>
                      <div
                        className="agent-model-chat__message agent-model-chat__message--model"
                        aria-label="Model assistant response"
                      >
                        <AgentChatTrace
                          items={turn.intermediate}
                          toolCalls={turn.tool_calls}
                          live={status !== null}
                          markdownContext={markdownContext}
                          editProposals={turnEditProposals}
                          approvingEdit={
                            approveEdit.isPending &&
                            turnEditProposals.some(
                              (proposal) =>
                                approveEdit.variables?.proposalId ===
                                proposal.id,
                            )
                          }
                          rejectingEdit={
                            rejectEdit.isPending &&
                            turnEditProposals.some(
                              (proposal) =>
                                rejectEdit.variables?.proposalId ===
                                proposal.id,
                            )
                          }
                          onApproveEdit={(proposal) => {
                            setSubmitError(null);
                            void approveEdit
                              .mutateAsync({
                                proposalId: proposal.id,
                                companyId,
                                versionId,
                                baseRevision: proposal.base_revision,
                                operations: proposal.operations,
                              })
                              .catch((error: unknown) =>
                                setSubmitError(
                                  error instanceof Error
                                    ? error.message
                                    : "The model edit could not be approved.",
                                ),
                              );
                          }}
                          onRejectEdit={(proposal) => {
                            setSubmitError(null);
                            void rejectEdit
                              .mutateAsync({ proposalId: proposal.id })
                              .catch((error: unknown) =>
                                setSubmitError(
                                  error instanceof Error
                                    ? error.message
                                    : "The model edit could not be rejected.",
                                ),
                              );
                          }}
                        />
                        {turn.tool_calls
                          .filter(
                            (toolCall) =>
                              toolCall.name === SHOW_WIDGET_TOOL_NAME,
                          )
                          .map((toolCall) => (
                            <AgentChatWidget
                              key={`${toolCall.sequence}-${toolCall.call_id}`}
                              toolCall={toolCall}
                              turnState={turn.state}
                              onSendPrompt={(prompt) =>
                                void sendMessage(prompt)
                              }
                            />
                          ))}
                        {status && pendingTurnProposal === undefined && (
                          <div
                            className="agent-model-chat__waiting"
                            role="status"
                            aria-label={status}
                          >
                            <span aria-hidden="true" />
                            <span aria-hidden="true" />
                            <span aria-hidden="true" />
                          </div>
                        )}
                        {turn.state === "failed" && (
                          <div
                            className="agent-model-chat__failure"
                            role="alert"
                          >
                            <p>
                              {turn.safe_message ??
                                "The model assistant could not answer this question."}
                            </p>
                            {!activeTurn && (
                              <IconButton
                                type="button"
                                tone="quiet"
                                size="sm"
                                label="Try again"
                                tooltip="Try again"
                                icon={<RetryIcon />}
                                onClick={() => void sendMessage(turn.question)}
                              />
                            )}
                          </div>
                        )}
                        {turn.state === "succeeded" && turn.answer_markdown && (
                          <>
                            <AgentChatMarkdown
                              source={turn.answer_markdown}
                              context={markdownContext}
                            />
                            {turn.insufficient_evidence && (
                              <small className="agent-model-chat__evidence-warning">
                                The pinned model and documents did not contain
                                enough evidence for a complete answer.
                              </small>
                            )}
                            <div className="agent-model-chat__answer-actions">
                              <IconButton
                                type="button"
                                tone="quiet"
                                size="sm"
                                label="Copy answer"
                                tooltip="Copy answer"
                                icon={<CopyIcon />}
                                onClick={() =>
                                  copyChatText(
                                    turn.answer_markdown ?? "",
                                    "Answer copied.",
                                    "The answer could not be copied.",
                                  )
                                }
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={transcriptEnd} />
              </div>

              <form className="agent-model-chat__composer" onSubmit={submit}>
                <div className="agent-model-chat__composer-field">
                  <TextArea
                    label="Ask about this model"
                    value={message}
                    rows={3}
                    maxLength={8_000}
                    disabled={
                      activeTurn ||
                      submitting ||
                      revisionLocked ||
                      pendingProposal !== undefined
                    }
                    placeholder={
                      revisionLocked
                        ? "Wait for model edits to finish saving."
                        : pendingProposal
                          ? "Approve or reject the pending model edit to continue."
                          : mode === "ask"
                            ? "How does the revenue forecast bridge from the latest actual year?"
                            : "Describe the model change to make."
                    }
                    onValueChange={setMessage}
                    onKeyDown={handleComposerKey}
                  />
                  <div className="agent-model-chat__composer-actions">
                    <Selector
                      className={`agent-model-chat__mode-selector agent-model-chat__mode-selector--${mode}`}
                      ariaLabel={`Chat mode: ${CHAT_MODE_OPTIONS.find((option) => option.value === mode)?.label ?? "Ask"}`}
                      options={CHAT_MODE_OPTIONS}
                      value={mode}
                      triggerContent={<ChatModeTrigger mode={mode} />}
                      portalMenu
                      onValueChange={(nextMode) => {
                        setMode(nextMode);
                        storeModelChatMode(nextMode);
                      }}
                      disabled={
                        activeTurn ||
                        submitting ||
                        revisionLocked ||
                        pendingProposal !== undefined
                      }
                    />
                    <IconButton
                      type="submit"
                      tone="primary"
                      size="md"
                      label="Send message"
                      tooltip="Send message"
                      icon={<SendIcon />}
                      containerClassName="agent-model-chat__send"
                      disabled={
                        !message.trim() ||
                        activeTurn ||
                        submitting ||
                        revisionLocked ||
                        pendingProposal !== undefined
                      }
                      aria-busy={submitting || activeTurn}
                    />
                  </div>
                </div>
              </form>
              {submitError && (
                <p className="agent-model-chat__submit-error" role="alert">
                  {submitError}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {previewDocument && (
        <AgentSummaryDocumentPreview
          companyId={companyId}
          document={previewDocument}
          onClose={() => setPreviewDocument(null)}
        />
      )}
    </section>
  );
}
